import { useState } from 'react'
import { genres, years } from '@/mocks/movies'

export interface FilterValue {
  genre: string
  year: string
  sort: string
}

interface FilterBarProps {
  value: FilterValue
  onChange: (patch: Partial<FilterValue>) => void
  onReset: () => void
}

const sortOptions = [
  { key: 'latest', label: '最新', icon: 'ri-time-line' },
  { key: 'hot', label: '热门', icon: 'ri-fire-line' },
]

export default function FilterBar({ value, onChange, onReset }: FilterBarProps) {
  const [expanded, setExpanded] = useState(false)

  const activeCount =
    (value.genre !== 'all' ? 1 : 0) +
    (value.year !== 'all' ? 1 : 0) +
    (value.sort !== 'latest' ? 1 : 0)

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700"
        >
          <i className="ri-filter-3-line" />
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-1.5 text-[10px] text-background-50">
              {activeCount}
            </span>
          )}
          <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 hover:text-accent-700"
        >
          重置
        </button>
      </div>

      <div
        className={`${expanded ? 'mt-4 flex' : 'hidden'} flex-col gap-4 md:mt-0 md:flex`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <span className="shrink-0 pt-1.5 text-xs text-foreground-500 sm:w-14">类型</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onChange({ genre: 'all' })}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                value.genre === 'all'
                  ? 'border-primary-800 bg-primary-800 text-background-50'
                  : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
              }`}
            >
              全部
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                type="button"
                onClick={() => onChange({ genre })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  value.genre === genre
                    ? 'border-primary-800 bg-primary-800 text-background-50'
                    : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="shrink-0 text-xs text-foreground-500 sm:w-14">年份</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onChange({ year: 'all' })}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                value.year === 'all'
                  ? 'border-primary-800 bg-primary-800 text-background-50'
                  : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
              }`}
            >
              全部
            </button>
            {years.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => onChange({ year: String(year) })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 font-label text-xs tracking-wide transition-colors ${
                  value.year === String(year)
                    ? 'border-primary-800 bg-primary-800 text-background-50'
                    : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="shrink-0 text-xs text-foreground-500 sm:w-14">排序</span>
          <div className="inline-flex w-fit items-center gap-1 rounded-full bg-background-200 px-1 py-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onChange({ sort: option.key })}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  value.sort === option.key
                    ? 'bg-background-50 font-medium text-primary-800'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={option.icon} />
                {option.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={onReset}
            className="hidden cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 hover:text-accent-700 sm:ml-auto sm:flex"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  )
}